/**
 * High-Level Drill Operations
 * 
 * This module provides high-level operations for the drill library,
 * including import/export, tag management, and batch operations.
 * All operations use the centralized DrillDatabase for consistency.
 */

import { Drill, DrillFormDrill } from '../types/drill-types';
import { DrillDatabase } from './drill-database';
import { exportDrillsToCSV } from '@/utils/drill-export-csv';
import { parseCSVFile, DrillImportData } from '@/utils/drill-import-csv';
import { loadTaxonomy, resetTaxonomyToDefaults } from '@/lib/taxonomy-loader';
import { loadTagTree, writeTaxonomyToStorage } from '@/lib/tag-system/json-handler';
import type { TagTreeJSON } from '@/lib/tag-system/json-handler';

export type ImportMode = 'skip' | 'overwrite' | 'reimport';

// Import centralized video matching utilities
import { extractVideoId, areSameVideoById } from './video-matcher.js';

/**
 * Extract YouTube video ID from URL
 * @deprecated Use extractVideoId from video-matcher.ts instead
 * This function is kept for backward compatibility but delegates to the centralized implementation
 */
export const extractYouTubeVideoId = (urlString?: string | null): string | null => {
  return urlString ? extractVideoId(urlString) : null;
};
import logger from '@/lib/logging';

/**
 * Check if two drills are duplicates based on YouTube video ID
 * Uses centralized video matcher utility
 */
export const isDuplicateDrill = (drill1: Partial<Drill>, drill2: Partial<Drill>): boolean => {
  return areSameVideoById(drill1, drill2);
};

/**
 * Drill Operations Manager
 * Provides high-level operations using DrillDatabase
 */
export class DrillOperations {
  constructor(private db: DrillDatabase) {}

  /**
   * Update drill from form data
   */
  async updateDrillFromForm(drillId: string, formData: Partial<DrillFormDrill>) {
    const updateData: Partial<Drill> = {
      ...formData
    };
    
    // Remove creation fields from update (preserve original)
    delete updateData.createdBy;
    delete updateData.createdAt;

    return await this.db.updateDrill(drillId, updateData);
  }

  /**
   * Import drills from file content
   */
  async importFromFile(
    content: string, 
    fileName: string,
    mode: ImportMode = 'overwrite',
    onProgress?: (progress: number) => void
  ): Promise<{
    success: number;
    updated: number;
    skipped: number;
    errors: string[];
  }> {
    try {
      // Parse file content
      onProgress?.(10);
      const importData = await this.parseImportFile(content, fileName);
      
      if (mode === 'reimport') {
        // Delete all existing drills first
        logger.debug('🔴 REIMPORT MODE: Deleting ALL existing drills from shared library...');
        onProgress?.(15);
        await this.db.deleteAllDrills();
        logger.debug('✅ REIMPORT MODE: All drills deleted');
        
        // Reset taxonomy to defaults (delete all custom tags)
        logger.debug('🔴 REIMPORT MODE: Resetting taxonomy to defaults (deleting all custom tags)...');
        onProgress?.(20);
        await resetTaxonomyToDefaults();
        logger.debug('✅ REIMPORT MODE: Taxonomy reset to defaults, now ensuring new tags from CSV...');
      }
      
      // Collect all tags from CSV and ensure they exist in taxonomy
      // In reimport mode this runs AFTER reset so new tag values from CSV are added
      if (importData.drills.length > 0) {
        logger.debug('🔍 Checking for missing tags in CSV import...');
        await this.ensureTagsExistInTaxonomy(importData.drills);
      }

      // Get existing drills for duplicate detection
      onProgress?.(30);
      const existingDrills = mode !== 'reimport' ? await this.db.getAllDrills() : [];
      
      let success = 0;
      let updated = 0;
      let skipped = 0;
      const errors: string[] = [];
      const totalDrills = importData.drills.length;

      logger.debug(`📦 Starting import of ${totalDrills} drills in ${mode} mode...`);

      for (let i = 0; i < totalDrills; i++) {
        const drill = importData.drills[i];
        const progress = 30 + ((i / totalDrills) * 60);
        onProgress?.(Math.round(progress));

        // Log progress every 50 drills or on specific milestones
        if (i % 50 === 0 || i === totalDrills - 1) {
          logger.debug(`📊 Import progress: ${i + 1}/${totalDrills} (${Math.round((i / totalDrills) * 100)}%) - Success: ${success}, Errors: ${errors.length}`);
        }

        try {
          // Validate drill data before processing
          if (!drill) {
            errors.push(`Drill at index ${i} is null or undefined`);
            logger.error(`❌ Drill at index ${i} is null or undefined`);
            continue;
          }

          // Use centralized video matcher to find duplicates
          const duplicate = existingDrills.find(existing => 
            isDuplicateDrill(drill, existing)
          );

          if (duplicate) {
            if (mode === 'skip') {
              skipped++;
              continue;
            } else if (mode === 'overwrite') {
              // Update existing drill, preserving creation audit
              await this.db.updateDrill(duplicate.id, {
                ...drill,
                createdBy: duplicate.createdBy,
                createdAt: duplicate.createdAt
              });
              updated++;
              continue;
            }
          }

          // Create new drill with validated data
          const drillData: Omit<Drill, 'id'> = {
            name: drill.name || 'Unnamed Drill',
            description: drill.description || '',
            videoUrl: drill.videoUrl || '',
            transcript: drill.transcript || '',
            mainCategory: drill.mainCategory || 'General',
            tags: drill.tags || {},
            createdBy: drill.createdBy || 'imported',
            createdAt: drill.createdAt || new Date().toISOString(),
            changedBy: drill.changedBy,
            changedAt: drill.changedAt
          };

          await this.db.createDrill(drillData);
          success++;
        } catch (error) {
          const errorMsg = `Failed to import drill #${i + 1} "${drill?.name || 'unknown'}": ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMsg);
          logger.error(`❌ ${errorMsg}`);
          logger.error('   Drill data:', JSON.stringify(drill, null, 2).substring(0, 500));
          
          // If we hit too many errors, stop the import
          if (errors.length >= 10) {
            logger.error(`🛑 Import stopped: Too many errors (${errors.length}). Last successful: ${success}`);
            break;
          }
        }
      }

      logger.debug(`✅ Import completed: ${success} created, ${updated} updated, ${skipped} skipped, ${errors.length} errors`);
      
      onProgress?.(100);
      return { success, updated, skipped, errors };
    } catch (error) {
      throw new Error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Export drills to CSV file
   */
  async exportToCSV(): Promise<void> {
    const drills = await this.db.exportDrills();
    const taxonomyData = await loadTaxonomy();
    const taxonomy = taxonomyData.categories || {};
    
    // Export with all audit fields
    exportDrillsToCSV(drills, taxonomy);
  }

  /**
   * Export drills to JSON
   */
  async exportToJSON(): Promise<string> {
    const drills = await this.db.exportDrills();
    
    const exportData = {
      version: '2.0',
      exportedAt: new Date().toISOString(),
      drills: drills
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Ensure all tags from CSV import exist in taxonomy
   * Creates missing categories and options automatically
   */
  private async ensureTagsExistInTaxonomy(drills: Drill[]): Promise<void> {
    try {
      // Load current taxonomy (both formats for compatibility)
      const tagTree = await loadTagTree();
      const taxonomy = await loadTaxonomy();
      
      // Collect all unique tags from all drills
      const tagsByCategory = new Map<string, Set<string>>();
      
      for (const drill of drills) {
        if (!drill.tags) continue;
        
        for (const [categoryName, tagValue] of Object.entries(drill.tags)) {
          if (!tagsByCategory.has(categoryName)) {
            tagsByCategory.set(categoryName, new Set());
          }
          
          const categorySet = tagsByCategory.get(categoryName)!;
          
          // Handle both single values and arrays
          if (Array.isArray(tagValue)) {
            tagValue.forEach(v => {
              if (v && typeof v === 'string') {
                categorySet.add(v.trim());
              }
            });
          } else if (tagValue && typeof tagValue === 'string') {
            categorySet.add(tagValue.trim());
          }
        }
      }
      
      // Check each category and add missing options
      let hasChanges = false;
      const updatedTree: TagTreeJSON = {
        ...tagTree,
        categories: { ...tagTree.categories }
      };
      
      for (const [categoryName, tagValues] of tagsByCategory) {
        // Check both tagTree and taxonomy for existing category
        const existingCategoryInTree = tagTree.categories[categoryName];
        const existingCategoryInTaxonomy = taxonomy.categories[categoryName];
        const existingCategory = existingCategoryInTree || existingCategoryInTaxonomy;
        
        // Get existing options from tagTree (preferred) or taxonomy
        const existingOptions = existingCategoryInTree?.options || existingCategoryInTaxonomy?.options || [];
        
        // Extract option labels (handle both string and TagOption formats)
        const existingOptionsSet = new Set<string>();
        const existingOptionLabels: string[] = [];
        for (const opt of existingOptions) {
          const label = typeof opt === 'string' ? opt : (opt as any).label || String(opt);
          existingOptionsSet.add(label);
          existingOptionLabels.push(label);
        }
        
        // Check if category exists in taxonomy
        if (!existingCategory) {
          // Category doesn't exist - create it
          logger.debug(`📝 Creating new category "${categoryName}" with ${tagValues.size} options`);
          updatedTree.categories[categoryName] = {
            type: 'select', // Default to select, can be changed later
            description: `Auto-created during CSV import`,
            options: Array.from(tagValues)
          };
          hasChanges = true;
        } else {
          // Category exists - check for missing options
          const missingOptions: string[] = [];
          for (const tagValue of tagValues) {
            if (!existingOptionsSet.has(tagValue)) {
              missingOptions.push(tagValue);
            }
          }
          
          if (missingOptions.length > 0) {
            logger.debug(`📝 Adding ${missingOptions.length} missing options to category "${categoryName}": ${missingOptions.join(', ')}`);
            const categoryType = existingCategoryInTree?.type || existingCategoryInTaxonomy?.type || 'select';
            const categoryDescription = existingCategoryInTree?.description || existingCategoryInTaxonomy?.description;
            updatedTree.categories[categoryName] = {
              type: categoryType,
              ...(categoryDescription && { description: categoryDescription }),
              options: [...existingOptionLabels, ...missingOptions]
            };
            hasChanges = true;
          }
        }
      }
      
      // Save updated taxonomy if there were changes
      if (hasChanges) {
        updatedTree.lastModified = new Date().toISOString();
        writeTaxonomyToStorage(JSON.stringify(updatedTree, null, 2));
        logger.debug('✅ Taxonomy updated with new tags from CSV import');
      } else {
        logger.debug('✅ All tags from CSV already exist in taxonomy');
      }
    } catch (error) {
      logger.error('⚠️ Failed to ensure tags exist in taxonomy:', error);
      // Don't throw - allow import to continue even if tag creation fails
    }
  }

  /**
   * Parse import file (CSV or JSON)
   */
  private async parseImportFile(content: string, fileName?: string): Promise<DrillImportData> {
    const isCSV = fileName?.toLowerCase().endsWith('.csv') || 
                  content.trim().startsWith('Title') || 
                  (content.includes(',') && !content.trim().startsWith('[') && !content.trim().startsWith('{'));
    
    if (isCSV) {
      return parseCSVFile(content);
    } else {
      try {
        const data = JSON.parse(content);
        
        if (Array.isArray(data)) {
          return {
            drills: data,
            duplicateCount: 0,
            exportedAt: undefined,
            version: undefined
          };
        } else if (data.drills && Array.isArray(data.drills)) {
          return {
            drills: data.drills,
            duplicateCount: 0,
            exportedAt: data.exportedAt,
            version: data.version
          };
        }
        
        throw new Error('Invalid file format');
      } catch (error) {
        throw new Error(`Invalid JSON file: ${error instanceof Error ? error.message : 'Syntax error'}`);
      }
    }
  }

  /**
   * Batch tag operations
   */
  async batchUpdateTags(
    drillIds: string[],
    operation: 'add' | 'remove' | 'replace',
    tags: { [key: string]: string | string[] }
  ): Promise<void> {
    if (operation === 'replace') {
      return await this.db.bulkUpdateTags(drillIds, tags);
    }

    const drills = await this.db.getAllDrills();
    const drillsMap = new Map(drills.map(d => [d.id, d]));
    
    const updates = drillIds.map(id => {
      const drill = drillsMap.get(id);
      if (!drill) return null;
      
      let updatedTags = { ...(drill.tags || {}) };
      
      if (operation === 'add') {
        updatedTags = { ...updatedTags, ...tags };
      } else if (operation === 'remove') {
        Object.keys(tags).forEach(key => {
          delete updatedTags[key];
        });
      }
      
      return { id, data: { tags: updatedTags } };
    }).filter(Boolean) as { id: string; data: Partial<Drill> }[];

    if (updates.length > 0) {
      return await this.db.bulkUpdateDrills(updates);
    }
  }

  /**
   * Get drill statistics
   */
  async getStatistics(): Promise<{
    totalDrills: number;
    byCategory: { [key: string]: number };
    byTags: { [key: string]: number };
    recentlyAdded: number;
    recentlyModified: number;
  }> {
    const drills = await this.db.getAllDrills();
    const now = new Date();
    const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    const byCategory: { [key: string]: number } = {};
    const byTags: { [key: string]: number } = {};
    let recentlyAdded = 0;
    let recentlyModified = 0;

    drills.forEach(drill => {
      // Count by category
      const category = drill.mainCategory || 'Uncategorized';
      byCategory[category] = (byCategory[category] || 0) + 1;

      // Count by tags
      if (drill.tags) {
        Object.keys(drill.tags).forEach(tagKey => {
          byTags[tagKey] = (byTags[tagKey] || 0) + 1;
        });
      }

      // Count recently added
      if (drill.createdAt && new Date(drill.createdAt) > dayAgo) {
        recentlyAdded++;
      }

      // Count recently modified
      if (drill.changedAt && new Date(drill.changedAt) > dayAgo) {
        recentlyModified++;
      }
    });

    return {
      totalDrills: drills.length,
      byCategory,
      byTags,
      recentlyAdded,
      recentlyModified
    };
  }

  /**
   * Search drills by various criteria
   */
  async searchDrills(criteria: {
    searchTerm?: string;
    category?: string;
    tags?: { [key: string]: string | string[] };
    createdBy?: string;
    modifiedAfter?: Date;
  }): Promise<Drill[]> {
    const drills = await this.db.getAllDrills();
    
    return drills.filter(drill => {
      // Search term in name or description
      if (criteria.searchTerm) {
        const term = criteria.searchTerm.toLowerCase();
        const inName = drill.name?.toLowerCase().includes(term);
        const inDesc = drill.description?.toLowerCase().includes(term);
        if (!inName && !inDesc) return false;
      }

      // Category filter
      if (criteria.category && drill.mainCategory !== criteria.category) {
        return false;
      }

      // Tags filter
      if (criteria.tags) {
        for (const [key, value] of Object.entries(criteria.tags)) {
          if (!drill.tags || drill.tags[key] !== value) {
            return false;
          }
        }
      }

      // Created by filter
      if (criteria.createdBy && drill.createdBy !== criteria.createdBy) {
        return false;
      }

      // Modified after filter
      if (criteria.modifiedAfter) {
        const modifiedDate = drill.changedAt ? new Date(drill.changedAt) : new Date(drill.createdAt);
        if (modifiedDate < criteria.modifiedAfter) {
          return false;
        }
      }

      return true;
    });
  }
}

/**
 * Factory function to create DrillOperations instance
 */
export function createDrillOperations(db: DrillDatabase): DrillOperations {
  return new DrillOperations(db);
}
